import {
  ComboBox,
  DefaultButton,
  IComboBox,
  IComboBoxOption,
  Icon, TextField,
  Toggle,
} from "@fluentui/react";
import { Helper } from "@taskpane/helpers";
import { useStore } from "@taskpane/hooks";
import { setColor } from "@taskpane/theme";
import { onFormatDate, onParseDateFromString, setApiURL } from "@taskpane/utils";
import { comboBoxHeight, comboBoxWidth, standardDateFormat } from "@taskpane/types";
import moment from "moment";
import React, { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ChoiceFormulaWrapper from "../../features/choiceFormulaWrapper";
import { GLBalanceFormula } from "../../features/glBalanceFormula";
import { PMDatePicker } from "@taskpane/components";
import { Endpoints } from "src/taskpane/core/endpoints";

interface GLBalanceWizardProps {
  onBackClick: () => void;
}

let parsedFormulaInst = null;

export const GLBalanceWizard = (props: GLBalanceWizardProps) => {
  const { t } = useTranslation();
  const { token } = useStore();

  const [companyListOptions, setCompanyListOptions] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [selCompanyKey, setSelCompanyKey] = useState("");
  const [selCompanyText, setSelCompanyText] = useState("");
  const [selCompanyName, setSelCompanyName] = useState("");
  const [selFiscalYear, setSelFiscalYear] = useState("");
  const [selCompanyError, setSelCompanyError] = useState(null);
  //
  const [accountListOptions, setAccountListOptions] = useState([]);
  const [accountList, setAccountList] = useState([]);
  const [account, setAccount] = useState("");
  const [accountError, setAccountError] = useState(null);
  //
  const [currencyListOptions, setCurrencyListOptions] = useState([]);
  const [currencyList, setCurrencyList] = useState([]);  
  const [currency, setCurrency] = useState("");
  //
  const [withProvisionalEntries, setWithProvisionalEntries] = useState(true);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateFromCell, setDateFromCell] = useState("");
  const [dateTo, setDateTo] = useState(null);
  const [dateToCell, setDateToCell] = useState("");
  const [fromDateError, setFromDateError] = useState(null);
  const [toDateError, setToDateError] = useState(null);
  const [toggleFromDate, setToggleFromDate] = useState(true);
  const [toggleToDate, setToggleToDate] = useState(true);
  //
  const [debits, setDebits] = useState(true);
  const [credits, setCredits] = useState(true);
  const [originalCurrency, setOriginalCurrency] = useState(false);

  useEffect(() => {
    const fetchFiscalYears = async () => {
      const url = setApiURL(Endpoints.getFiscalYears());
      const val = await fetch(url, { method: 'GET', headers: { 'X-Apikey': token } }).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        };
        return response.json();
      });
      setCompanyList(val);
    }
    fetchFiscalYears();
  }, []);

  useEffect(() => {
    Excel.run(async (context) => {
      const cell = context.workbook.getActiveCell();
      cell.load("formulas, values");
      await context.sync();
      parsedFormulaInst = null;
      if (cell) {
        const fWrapper = new ChoiceFormulaWrapper();
        const unwrapped = fWrapper.unwrap(cell.formulas[0][0]);
        if (unwrapped) {
          parsedFormulaInst = new GLBalanceFormula(null).parse(unwrapped.formula) as GLBalanceFormula;
          if (parsedFormulaInst) {
            const valCompanyName = Helper.hasExcelConcatDecoration(parsedFormulaInst.formulaData.companyName)
              ? Helper.removeExcelConcatDecoration(parsedFormulaInst.formulaData.companyName)
              : parsedFormulaInst.formulaData.companyName;
            const valFiscalYear = Helper.hasExcelConcatDecoration(parsedFormulaInst.formulaData.fiscalYear)
              ? Helper.removeExcelConcatDecoration(parsedFormulaInst.formulaData.fiscalYear)
              : parsedFormulaInst.formulaData.fiscalYear;
            const company = companyList.find(c => c.CompanyName === valCompanyName && c.FiscalYearCode === valFiscalYear);
            if (company) {
              setSelCompanyText(company.FiscalYearDescription);
              setSelCompanyKey(company.ID);
            } else {
              setSelCompanyText(valCompanyName + " " + valFiscalYear);
            }
            setSelCompanyName(valCompanyName);
            setSelFiscalYear(valFiscalYear);

            setAccount(
              Helper.hasExcelConcatDecoration(parsedFormulaInst.formulaData.account)
                ? Helper.removeExcelConcatDecoration(parsedFormulaInst.formulaData.account)
                : parsedFormulaInst.formulaData.account
            );

            setCurrency(
              Helper.hasExcelConcatDecoration(parsedFormulaInst.formulaData.currency)
                ? Helper.removeExcelConcatDecoration(parsedFormulaInst.formulaData.currency)
                : parsedFormulaInst.formulaData.currency
            );

            if (Helper.hasExcelConcatDecoration(parsedFormulaInst.formulaData.fromDate)) {
              setDateFromCell(Helper.removeExcelConcatDecoration(parsedFormulaInst.formulaData.fromDate));
              setToggleFromDate(false);
              setDateFrom(null);
            } else {
              setDateFromCell(null);
              setToggleFromDate(true);
              setDateFrom(moment(parsedFormulaInst.formulaData.fromDate).toDate());
            }

            if (Helper.hasExcelConcatDecoration(parsedFormulaInst.formulaData.toDate)) {
              setDateToCell(Helper.removeExcelConcatDecoration(parsedFormulaInst.formulaData.toDate));
              setToggleToDate(false);
              setDateTo(null);
            } else {
              setDateToCell("");
              setToggleToDate(true);
              setDateTo(moment(parsedFormulaInst.formulaData.toDate).toDate());
            }

            setWithProvisionalEntries(parsedFormulaInst.formulaData.withProvisionalEntries === "true");
            setOriginalCurrency(parsedFormulaInst.formulaData.originalCurrency === "true");
          }
        }
      }
    });

    if (selCompanyKey !== "") {
      const fetchAccountList = async () => {
        const url = setApiURL(Endpoints.getCOA(selCompanyName, selFiscalYear));
        const val = await fetch(url, { method: 'GET', headers: { 'X-Apikey': token } }).then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          };
          return response.json();
        });
        setAccountList(val);
      }
      fetchAccountList();

      const fetchCurrencyList = async () => {
        const url = setApiURL(Endpoints.getCurrencies(selCompanyName, selFiscalYear));
        const val = await fetch(url, { method: 'GET', headers: { 'X-Apikey': token } }).then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          };
          return response.json();
        });
        setCurrencyList(val);
      }
      fetchCurrencyList();
    }
  }, [selCompanyKey]);



  const handleBackOnClick = () => {
    props.onBackClick();
  };

  const validate = () => {
    let result = true;
    if (!selCompanyText.trim()) {
      setSelCompanyError(t("pleaseChooseValue"));
      result = false;
    }

    if (!account) {
      setAccountError(t("pleaseChooseValue"));
      result = false;
    }

    if (toggleFromDate) {
      if (!dateFrom) {
        setFromDateError(t("pleaseEnterValue"));
        result = false;
      }

      if (dateFrom > dateTo && toggleToDate) {
        setFromDateError(t("dateFromCannotBeAfterDateTo"));
        result = false;
      }
    }

    if (toggleToDate) {
      if (!dateTo) {
        setToDateError(t("pleaseEnterValue"));
        result = false;
      }
    }

    if (!dateFromCell && !toggleFromDate) {
      setFromDateError(t("pleaseEnterValue"));
      result = false;
    }

    if (!dateToCell && !toggleToDate) {
      setToDateError(t("pleaseEnterValue"));
      result = false;
    }

    return result;
  };

  const onOkClick = async () => {
    try {
      if (!validate()) return;

      let actualAccount = account;
      let actualSelCompany = selCompanyName;
      let actualSelFiscalYear = selFiscalYear;
      let actualCurrency = currency;

      await Excel.run(async (context) => {
        if ((await Helper.isCellRef(selCompanyName, context)).isCellRef) {
          actualSelCompany = Helper.decorateWithExcelConcat(selCompanyName);
        }

        if ((await Helper.isCellRef(selFiscalYear, context)).isCellRef) {
          actualSelFiscalYear = Helper.decorateWithExcelConcat(selFiscalYear);
        }

        if ((await Helper.isCellRef(account, context)).isCellRef) {
          actualAccount = Helper.decorateWithExcelConcat(account);
        }

        if ((await Helper.isCellRef(currency, context)).isCellRef) {
          actualCurrency = Helper.decorateWithExcelConcat(currency);
        }
      });

      const formula = new GLBalanceFormula(token);
      formula.formulaData = {
        companyName: actualSelCompany,
        fiscalYear: actualSelFiscalYear,
        fromDate: !toggleFromDate ? Helper.decorateWithExcelConcat(dateFromCell) : moment(dateFrom).format(standardDateFormat),
        toDate: !toggleToDate ? Helper.decorateWithExcelConcat(dateToCell) : moment(dateTo).format(standardDateFormat),
        account: actualAccount,
        currency: actualCurrency,
        withProvisionalEntries: withProvisionalEntries.toString(),
        originalCurrency: originalCurrency.toString(),
        debits: debits.toString(),
        credits: credits.toString(),
      };
      await Excel.run(async (context: Excel.RequestContext) => {
        const cell = context.workbook.getActiveCell();
        if (cell) {
          let displayFormulasMode = 1;
          const formulaValue = new ChoiceFormulaWrapper().wrap(displayFormulasMode, 0, formula.generate());

          cell.formulas = [[formulaValue]];

          props.onBackClick();
        }
      });
    } catch (e) {
      console.error("GLBalanceWizard:OkClick", e);
    }
  };

  const onCompanyChange = (
    event: React.FormEvent<IComboBox>,
    option?: any,
    index?: number,
    value?: string
  ) => {
    parsedFormulaInst = null;
    if (option) {
      setSelCompanyKey(option.key as string);
      setSelCompanyText(option.text);
      setSelCompanyName(option.companyName);
      setSelFiscalYear(option.fiscalYearCode);
    }

    if (!option) return;
    const company = companyList.find((c) => c.ID === option.key);
    if (company) {
      setDateFrom(moment(company.StartDate).toDate());
      setDateTo(moment(company.EndDate).toDate());

      //dispatch(loadAccountList(company.CompanyName, company.FiscalYearCode));
      //dispatch(loadCurrencyList(company.CompanyName, company.FiscalYearCode));
    }
  };

  function onAccountChange(event: React.FormEvent<IComboBox>, option: IComboBoxOption, index: number, value: string) {
    if (option) {
      const foundItem = accountList.find(e => e.ID === option.key);
      if (foundItem) {
        setAccount(foundItem.AccountNumber);
        setCurrency(foundItem.CurrencyCode);
      }
    }
    else if (value) setAccount(value);
  }

  function onSelectFromDate(date: Date | null | undefined) {
    setDateFrom(date);
  }

  function onSelectToDate(date: Date | null | undefined) {
    setDateTo(date);
  }

  function onProvisionalEntriesToggle() {
    setWithProvisionalEntries(!withProvisionalEntries);
  }

  useEffect(() => {
    setCompanyListOptions(companyList.map((element) => ({ key: element.ID, text: element.FiscalYearDescription, companyName: element.CompanyName, fiscalYearCode: element.FiscalYearCode })));
  }, [companyList, setCompanyListOptions]);

  useEffect(() => {
    if (accountList && accountList.length > 0) {
      setAccountListOptions(accountList.map((e) => ({ key: e.ID, text: e.DisplayFormatedString })));
    }
  }, [accountList]);

  useEffect(() => {
    if (!parsedFormulaInst) return;
    setAccount(
      Helper.hasExcelConcatDecoration(parsedFormulaInst.formulaData.account)
        ? Helper.removeExcelConcatDecoration(parsedFormulaInst.formulaData.account)
        : parsedFormulaInst.formulaData.account
    );
  }, [accountListOptions]);

  useEffect(() => {
    if (currencyList && currencyList.length > 0) {
      setCurrencyListOptions(currencyList.map((e) => ({ key: e.Code, text: e.Code })));
    }
  }, [currencyList]);

  useEffect(() => {
    if (!parsedFormulaInst) return;
    setCurrency(
      Helper.hasExcelConcatDecoration(parsedFormulaInst.formulaData.currency)
        ? Helper.removeExcelConcatDecoration(parsedFormulaInst.formulaData.currency)
        : parsedFormulaInst.formulaData.currency
    );
  }, [currencyListOptions]);

  function onDateFromCellChange(event, newValue) {
    setDateFromCell(newValue);
  }

  function onDateToCellChange(event, newValue) {
    setDateToCell(newValue);
  }

  function onToggleFromDate(event, checked) {
    setFromDateError(null);
    setToggleFromDate(checked);
  }

  function onToggleToDate(event, checked) {
    setToDateError(null);
    setToggleToDate(checked);
  }

  const fromDateComp = (<>
    <PMDatePicker
      allowTextInput
      onSelectDate={onSelectFromDate}
      formatDate={onFormatDate}
      parseDateFromString={onParseDateFromString(dateTo)}
      value={dateFrom}
    />
  </>);

  const toDateComp = (<>
    <PMDatePicker
      allowTextInput
      onSelectDate={onSelectToDate}
      formatDate={onFormatDate}
      parseDateFromString={onParseDateFromString(dateTo)}
      value={dateTo}
    />
  </>);

  function onCurrencyChange(event: React.FormEvent<IComboBox>, option: IComboBoxOption, index: number, value: string) {
    if (option) {
      setCurrency(option.key as string);
    }
    else if (value) setCurrency(value);
  }

  function onOriginalCurrencyToggle() {
    setOriginalCurrency(!originalCurrency);
  }

  function onDebitsChange(event, checked) {
    if (!credits && !checked) setDebits(true)
    else setDebits(checked);
  }

  function onCreditsChange(event, checked) {
    if (!debits && !checked) setCredits(true)
    else setCredits(checked);
  }

  return (
    <div className="pt-4 col col-12">
      <div className="h-100 document">
        <div onClick={() => handleBackOnClick()} style={{ cursor: "pointer" }} className="d-flex">
          <Icon iconName="Back" className="ms-font-l" style={{ color: setColor("blue") }} />
          <span className="ms-font-l ms-2" style={{ color: setColor("blue") }}>
            {t("back")}
          </span>
        </div>

        <div className="mt-3 mb-2 ms-fontWeight-bold">
          <span>{t("glBalanceFormula")}</span>
        </div>

        <div className="mb-2">
          <ComboBox
            label={t("company")}
            selectedKey={selCompanyKey}
            options={companyListOptions}
            text={selCompanyText}
            onChange={onCompanyChange}
            allowFreeform={true}
            dropdownWidth={comboBoxWidth}
            calloutProps={{ calloutMaxHeight: comboBoxHeight }}
          ></ComboBox>
          {selCompanyError ? <span style={{ color: setColor("red") }}>{selCompanyError}</span> : ""}
        </div>

        <div className="mb-2">
          <ComboBox
            label={t("account")}
            text={account}
            onChange={onAccountChange}
            options={accountListOptions}
            allowFreeform={true}
            dropdownWidth={comboBoxWidth}
            calloutProps={{ calloutMaxHeight: comboBoxHeight }}
          ></ComboBox>
          {accountError ? <span style={{ color: setColor("red") }}>{accountError}</span> : ""}
        </div>

        <div className="mb-2">
          <ComboBox
            label={t("currency")}
            text={currency}
            onChange={onCurrencyChange}
            options={currencyListOptions}
            allowFreeform={true}
            dropdownWidth={comboBoxWidth}
            calloutProps={{ calloutMaxHeight: comboBoxHeight }}
          ></ComboBox>
        </div>

        <div className="mb-2">
          <div className="row">
            <Toggle
              label={t("fromDate")}
              checked={toggleFromDate}
              onChange={onToggleFromDate}
              onText={t('date')} offText={t('cell')}
            />
            {toggleFromDate ?
              (<div className="col-9">
                {fromDateComp}
              </div>)
              :
              (<div className="col-3">
                <TextField
                  value={dateFromCell}
                  onChange={onDateFromCellChange}
                />
              </div>)
            }
            {fromDateError ? <span style={{ color: setColor("red") }}>{fromDateError}</span> : ""}
          </div>
        </div>
        <div className="mb-2">
          <div className="row">
            <Toggle
              label={t("toDate")}
              onChange={onToggleToDate}
              checked={toggleToDate}
              onText={t('date')} offText={t('cell')}
            />
            {toggleToDate ?
              <div className="col-9">
                {toDateComp}
              </div> :
              <div className="col-3">
                <TextField
                  value={dateToCell}
                  onChange={onDateToCellChange}
                />
              </div>}
            {toDateError ? <span style={{ color: setColor("red") }}>{toDateError}</span> : ""}
          </div>
        </div>
        <div className="mb-2">
          <Toggle
            label={t("originalCurrency")}
            onClick={() => onOriginalCurrencyToggle()}
            checked={originalCurrency}
          ></Toggle>
        </div>
        <div className="mb-2">
          <Toggle
            label={t("includeProvisionalEntries")}
            onClick={() => onProvisionalEntriesToggle()}
            checked={withProvisionalEntries}
          ></Toggle>
        </div>

        <div className="mb-2">
          <Toggle label={t("debits")} checked={debits} onChange={onDebitsChange} />
        </div>
        <div className="mb-2">
          <Toggle label={t("credits")} checked={credits} onChange={onCreditsChange} />
        </div>

        <div className="mb-2">
          <DefaultButton onClick={onOkClick}>{t("validate")}</DefaultButton>
        </div>
      </div>
    </div>
  );
};

